import React, { useMemo } from 'react';
import { Text } from 'src/@vymo/ui/atoms';
import { Card } from 'src/@vymo/ui/blocks';
import SkeletonLoader from 'src/@vymo/ui/blocks/skelton';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Keys, locale } from 'src/i18n';
import { useAppSelector } from 'src/store/hooks';
import {
  getKeyMetricsBreakdownGraph,
  getKeyMetricsDetails,
  getKeyMetricsTrendGraph,
  getStatus,
} from '../../selector';
import { chartMargins } from '../constants';
import {
  getPerformanceAnalysisData,
  getReferenceTargetInfo,
  getTrendAnalysisData,
} from './queries';
import styles from '../index.module.scss';

function KeyMetricsOverview() {
  const trendGraphStatus = useAppSelector((state) =>
    getStatus(state, 'key_metrics_trend_graph'),
  );
  const breakdownGraphStatus = useAppSelector((state) =>
    getStatus(state, 'key_metrics_breakdown_graph'),
  );
  const trendGraph = useAppSelector(getKeyMetricsTrendGraph);
  const breakdownGraph = useAppSelector(getKeyMetricsBreakdownGraph);
  const keyMetricsDetails = useAppSelector(getKeyMetricsDetails);

  const performanceAnalysisData = useMemo(
    () => getPerformanceAnalysisData(breakdownGraph),
    [breakdownGraph],
  );

  const trendAnalysisData = useMemo(
    () => getTrendAnalysisData(trendGraph),
    [trendGraph],
  );

  const { referenceTarget, hasReferenceTarget } = useMemo(
    () => getReferenceTargetInfo(keyMetricsDetails),
    [keyMetricsDetails],
  );

  return (
    <Card classNames={styles.details__overview}>
      <Text type="h5">{locale(Keys.PERFORMANCE_OVERVIEW)}</Text>
      {breakdownGraphStatus === 'done' && (
        <div className={styles.details__overview__breakdownGraph}>
          <Text>{breakdownGraph?.body?.result?.title}</Text>
          <Text type="subText">{breakdownGraph?.body?.result?.sub_title}</Text>
          <div className={styles.details__overview__chartWrapper}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={performanceAnalysisData} margin={chartMargins}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  minTickGap={24}
                  tickMargin={8}
                  tickFormatter={(value) => String(value)?.slice(5)}
                  label={{
                    value:
                      breakdownGraph?.body?.result?.chart_meta?.x_axis_title ||
                      locale(Keys.DAYS),
                    position: 'insideBottom',
                    offset: -16,
                  }}
                />
                <YAxis
                  width={64}
                  domain={[
                    0,
                    (dataMax: number) =>
                      hasReferenceTarget
                        ? Math.max(dataMax, referenceTarget)
                        : dataMax,
                  ]}
                  label={{
                    value:
                      breakdownGraph?.body?.result?.chart_meta?.y_axis_title ||
                      '',
                    angle: -90,
                    position: 'insideLeft',
                    dx: -16,
                  }}
                />
                <Tooltip labelFormatter={(label) => String(label)} />
                <Legend
                  verticalAlign="top"
                  align="right"
                  wrapperStyle={{ top: 0 }}
                />
                {hasReferenceTarget ? (
                  <ReferenceLine
                    y={referenceTarget}
                    name={locale(Keys.GOAL)}
                    label={`${locale(Keys.GOAL)}: ${referenceTarget}`}
                    stroke="var(--chart-categorical-1)"
                    strokeWidth={2}
                  />
                ) : null}
                <Area
                  type="monotone"
                  dataKey="value"
                  name={locale(Keys.ACHIEVEMENT)}
                  stroke="var(--chart-categorical-1)"
                  fill="var(--chart-categorical-1)"
                  fillOpacity={0.28}
                  connectNulls
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {breakdownGraphStatus === 'loading' && <SkeletonLoader rect />}

      {trendGraphStatus === 'done' && (
        <div className={styles.details__overview__breakdownGraph}>
          <Text>
            {trendGraph?.body?.result?.title || locale(Keys.HISTORIC_GRAPH)}
          </Text>
          <Text type="subText">{trendGraph?.body?.result?.sub_title}</Text>
          <div className={styles.details__overview__chartWrapper}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendAnalysisData} margin={chartMargins}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  minTickGap={24}
                  tickMargin={8}
                  tickFormatter={(value) => String(value)?.slice(5)}
                  label={{
                    value:
                      trendGraph?.body?.result?.chart_meta?.x_axis_title ||
                      locale(Keys.MONTHS),
                    position: 'insideBottom',
                    offset: -16,
                  }}
                />
                <YAxis
                  width={64}
                  domain={[0, 'auto']}
                  label={{
                    value:
                      trendGraph?.body?.result?.chart_meta?.y_axis_title || '',
                    angle: -90,
                    position: 'insideLeft',
                    dx: -16,
                  }}
                />
                <Tooltip />
                <Legend
                  verticalAlign="top"
                  align="right"
                  wrapperStyle={{ top: 0 }}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  name={locale(Keys.ACHIEVEMENT)}
                  stroke="var(--chart-categorical-10)"
                  fill="var(--chart-categorical-10)"
                  fillOpacity={0.28}
                  connectNulls
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <Area
                  type="monotone"
                  dataKey="target"
                  name={locale(Keys.TARGET)}
                  stroke="var(--chart-categorical-6)"
                  fill="var(--chart-categorical-6)"
                  fillOpacity={0.18}
                  connectNulls
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {trendGraphStatus === 'loading' && <SkeletonLoader rect />}
    </Card>
  );
}

export default KeyMetricsOverview;
